import React, { useState, useEffect } from 'react';
import { BookOpen, Download, FileText, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Owner manuals & guides download block (email-gated, captures a lead), for the
// Technical Support page. Pulls the instruction-manual documents from the API.
export const OwnerManuals = ({ bg = 'bg-white', testid = 'owner-manuals' }) => {
  const [docs, setDocs] = useState([]);
  const [gateDoc, setGateDoc] = useState(null);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/documents?category=instruction-manual`)
      .then((r) => { if (Array.isArray(r.data)) setDocs(r.data); })
      .catch(() => {});
  }, []);

  const urlFor = (doc) => (doc.external_url ? doc.external_url : `${API_URL}${doc.file_url}`);

  const submitGate = async () => {
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email to get your download.'); return; }
    const url = urlFor(gateDoc);
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/document`, {
        email,
        document_title: gateDoc.title,
        download_url: gateDoc.external_url ? url : `${window.location.origin.replace(/\/$/, '')}${gateDoc.file_url}`,
        category: gateDoc.category,
      });
    } catch (e) { /* non-blocking */ }
    setSubmitting(false);
    window.open(url, '_blank', 'noopener, noreferrer');
    toast.success('Sent to your inbox, opening now too');
    setGateDoc(null);
    setEmail('');
  };

  if (!docs.length) return null;

  return (
    <section className={`py-20 section-padding ${bg}`} data-testid={testid}>
      <div className="max-w-5xl mx-auto">
        <span className="inline-flex items-center gap-2 text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">
          <BookOpen className="h-4 w-4" /> Owner resources
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Owner manuals &amp; guides</h2>
        <p className="text-slate-600 mt-3 max-w-2xl">Download your LyphLine freeze dryer manual and quick-start guide. Enter your email and we'll send a copy and open it for you.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {docs.map((doc) => (
            <div key={doc.id} className="bg-[#F1F5F9] border border-slate-200 rounded-2xl p-7 flex flex-col hover:shadow-lg transition-shadow" data-testid={`${testid}-item`}>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-lg bg-[#003456] flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5 text-[#CE8946]" /></span>
                <h3 className="text-lg font-bold text-[#003456]">{doc.title}</h3>
              </div>
              {doc.description && <p className="text-slate-600 text-sm mt-4 flex-1">{doc.description}</p>}
              <button
                onClick={() => { setGateDoc(doc); setEmail(''); }}
                className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-6 py-3 rounded-md mt-6 transition-colors"
                data-testid={`${testid}-download-${doc.id}`}
              >
                <Download className="h-4 w-4" /> Download PDF
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Email gate */}
      {gateDoc && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid={`${testid}-gate`}>
          <div className="bg-white rounded-2xl p-7 max-w-md w-full relative">
            <button onClick={() => setGateDoc(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" aria-label="Close"><X className="h-5 w-5" /></button>
            <h3 className="text-xl font-bold text-[#003456]">Get "{gateDoc.title}"</h3>
            <p className="text-slate-600 text-sm mt-2">Enter your email and we'll send you the download link and open it now.</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitGate(); }}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 rounded-md border border-slate-300 text-[#003456] text-sm mt-5 focus:outline-none focus:ring-2 focus:ring-[#CE8946]"
              data-testid={`${testid}-gate-email`}
              autoFocus
            />
            <button onClick={submitGate} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#003456] hover:bg-[#023F63] text-white font-bold px-6 py-3 rounded-md mt-4 transition-colors disabled:opacity-60" data-testid={`${testid}-gate-submit`}>
              {submitting ? 'Sending…' : (<>Get my download <Download className="h-4 w-4" /></>)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default OwnerManuals;
